import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Country, CountryFlat } from '../models/country';
import { LookupTableCache } from '../models/lookupTableCache';
import { Currency } from '../models/currency';
import { Index } from '../models/index';
import { IndustryFlat } from '../models/industry';
import { LutService } from './lut.service';
import { environment } from '../../environments/environment';

interface CustomerDirectorLookupsResponse {
  countries: CountryFlat[];
  nationalities: any[];
  professions: any[];
  industries: IndustryFlat[];
  industryExpertises: any[];
  boardMemberOccupations: any[];
  honorificTitles: any[];
  qualifications: any[];
  financialExpertises: any[];
  politicalPositions: any[];
  educationFields: any[];
}

interface CustomerCompanySearchLookupsResponse {
  countries: CountryFlat[];
  industries: IndustryFlat[];
  stockExchanges: { id: string; name: string }[];
  stockIndices: Index[];
}

@Injectable({
  providedIn: 'root'
})

export class LookupDataService  {
  private lutService = inject(LutService);
  private http = inject(HttpClient);

  private readonly LS_DATA_PREFIX = 'lookup_data_';
  private customerDirectorLookupsLoaded = false;
  private customerCompanySearchLookupsLoaded = false;

  private tables: { [key: string]: LookupTableCache<any> } = {};

  private countries: Country[] = [];

  constructor() {
    // Initialize known lookup tables
    this.initializeTable<CountryFlat>('countries');
    this.initializeTable<Currency>('currencies');
    this.initializeTable('nationalities');
    this.initializeTable('professions');
    this.initializeTable('honorificTitles');
    this.initializeTable('qualifications');
    this.initializeTable('financialExpertises');
    this.initializeTable('politicalPositions');
    this.initializeTable('industryExpertises');
    this.initializeTable('stockExchanges');
    this.initializeTable('stockIndices');
    this.initializeTable('industries');
    this.initializeTable('boardMemberOccupations');
    this.initializeTable('educationFields');
  }

  private initializeTable<T>(tableName: string): void {
    if (!this.tables[tableName]) {
      const subject = new BehaviorSubject<T[] | null>(null);
      this.tables[tableName] = {
        data: null,
        version: null,
        subject: subject,
        observable$: subject.asObservable(),
        isLoading: new BehaviorSubject<boolean>(false),
      };
    }
  }

  private getTableCache<T>(tableName: string): LookupTableCache<T> {
    if (!this.tables[tableName]) {
      console.warn(`Table ${tableName} not initialized. Initializing now.`);
      this.initializeTable<T>(tableName);
    }
    return this.tables[tableName] as LookupTableCache<T>;
  }

  // Public observable to get data
  getTableStream<T>(tableName: string): Observable<T[] | null> {
    return this.getTableCache<T>(tableName).observable$;
  }

  getVisibleCountries<T extends { isVisible: boolean } = Country>(): Observable<T[]> {
    return this.getTableStream<T>('countries').pipe(
      map(countries => countries ? countries.filter(c => c.isVisible) : [])
    );
  }

  // Public observable for loading status
  getTableLoadingState(tableName: string): Observable<boolean> {
    return this.getTableCache(tableName).isLoading.asObservable();
  }

  // Update cached table data and notify all subscribers
  updateTableData<T extends object>(tableName: string, data: T[]): void {
    const tableCache = this.getTableCache<T>(tableName);

    // Sort data alphabetically by 'name' property if it exists
    if (data && data.length > 0 && 'name' in data[0]) {
      data.sort((a: any, b: any) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    }

    // Update cache and localStorage
    tableCache.subject.next(data);
    tableCache.data = data;
    localStorage.setItem(this.LS_DATA_PREFIX + tableName, JSON.stringify(data));
  }

  // Method to load/check a lookup table
  async loadTable<T extends object>(tableName: string): Promise<void> {
    const tableCache = this.getTableCache<T>(tableName);

    if (tableCache.isLoading.value) {
      return; // Already loading
    }

    tableCache.isLoading.next(true);

    const dataKey = this.LS_DATA_PREFIX + tableName;

    // Try to load from localStorage first to populate subject if empty (e.g., on app init before HTTP)
    const localDataStr = localStorage.getItem(dataKey);

    if (localDataStr && tableCache.subject.value === null) {
        try {
            const localData = JSON.parse(localDataStr) as T[];
            tableCache.subject.next(localData); // Tentatively populate from local storage
        } catch (e) {
            console.error(`Error parsing localStorage data for ${tableName}:`, e);
            localStorage.removeItem(dataKey); // Clear corrupted data
        }
    }

    try {
      let serverData: T[];

      // Use LutService for specific tables
      if (tableName === 'countries') {
        serverData = await firstValueFrom(this.lutService.getCountries()) as T[];
      } else if (tableName === 'currencies') {
        serverData = await firstValueFrom(this.lutService.getCurrencies()) as T[];
      } else if (tableName === 'nationalities') {
        serverData = await firstValueFrom(this.lutService.getNationalities()) as T[];
      } else if (tableName === 'professions') {
        serverData = await firstValueFrom(this.lutService.getProfessions()) as T[];
      } else if (tableName === 'honorificTitles') {
        serverData = await firstValueFrom(this.lutService.getHonorificTitles()) as T[];
      } else if (tableName === 'qualifications') {
        serverData = await firstValueFrom(this.lutService.getQualifications()) as T[];
      } else if (tableName === 'financialExpertises') {
        serverData = await firstValueFrom(this.lutService.getFinancialExpertise()) as T[];
      } else if (tableName === 'politicalPositions') {
        serverData = await firstValueFrom(this.lutService.getPoliticalPositions()) as T[];
      } else if (tableName === 'industryExpertises') {
        serverData = await firstValueFrom(this.lutService.getIndustryExpertise()) as T[];
      } else if (tableName === 'stockExchanges') {
        serverData = await firstValueFrom(this.lutService.getStockExchanges()) as T[];
      } else if (tableName === 'stockIndices') {
        serverData = await firstValueFrom(this.lutService.getStockIndices()) as T[];
      } else if (tableName === 'industries') {
        serverData = await firstValueFrom(this.lutService.getIndustries()) as T[];
      } else if (tableName === 'boardMemberOccupations') {
        serverData = await firstValueFrom(this.lutService.getBoardMemberOccupations()) as T[];
      } else {
        throw new Error(`Unknown table: ${tableName}`);
      }

      // Sort data alphabetically by 'name' property if it exists
      if (serverData && serverData.length > 0 && 'name' in serverData[0]) {
        serverData.sort((a: any, b: any) => {
          const nameA = a.name?.toLowerCase() || '';
          const nameB = b.name?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
      }

      // Store data in localStorage
      localStorage.setItem(dataKey, JSON.stringify(serverData));
      tableCache.subject.next(serverData);
      tableCache.data = serverData;
    } catch (error) {
      console.error(`Error loading lookup table ${tableName}:`, error);
      // If HTTP fails but we have local data in subject (from tentative load), keep it.
      // If subject is still null, it means both localStorage and HTTP failed.
      if (tableCache.subject.value === null) {
           console.warn(`Table ${tableName} could not be loaded from server or localStorage.`);
      } else {
          console.warn(`Table ${tableName} using data from localStorage due to server error.`);
      }
      // Optionally, re-throw or handle error as per application needs
    } finally {
        tableCache.isLoading.next(false);
    }
  }

  /**
   * Fetch the bundled lookups needed by the portal directors search box and seed
   * the existing table caches so the rest of the app can use getTableStream as usual.
   * No-op after the first successful load in a session.
   */
  async loadCustomerDirectorLookups(): Promise<void> {
    if (this.customerDirectorLookupsLoaded) return;

    const response = await firstValueFrom(
      this.http.get<CustomerDirectorLookupsResponse>(`${environment.apiUrl}/customer/lookups/directors-search`)
    );

    this.updateTableData('countries', response.countries ?? []);
    this.updateTableData('nationalities', response.nationalities ?? []);
    this.updateTableData('professions', response.professions ?? []);
    this.updateTableData('industries', response.industries ?? []);
    this.updateTableData('industryExpertises', response.industryExpertises ?? []);
    this.updateTableData('boardMemberOccupations', response.boardMemberOccupations ?? []);
    this.updateTableData('honorificTitles', response.honorificTitles ?? []);
    this.updateTableData('qualifications', response.qualifications ?? []);
    this.updateTableData('financialExpertises', response.financialExpertises ?? []);
    this.updateTableData('politicalPositions', response.politicalPositions ?? []);
    this.updateTableData('educationFields', response.educationFields ?? []);

    this.customerDirectorLookupsLoaded = true;
  }

  /**
   * Fetch the bundled lookups needed by the portal companies search box and seed
   * the existing table caches so the rest of the app can use getTableStream as usual.
   * No-op after the first successful load in a session.
   */
  async loadCustomerCompanySearchLookups(): Promise<void> {
    if (this.customerCompanySearchLookupsLoaded) return;

    const response = await firstValueFrom(
      this.http.get<CustomerCompanySearchLookupsResponse>(`${environment.apiUrl}/customer/lookups/companies-search`)
    );

    this.updateTableData('countries', response.countries ?? []);
    this.updateTableData('industries', response.industries ?? []);
    this.updateTableData('stockExchanges', response.stockExchanges ?? []);
    this.updateTableData('stockIndices', response.stockIndices ?? []);

    this.customerCompanySearchLookupsLoaded = true;
  }
}
